import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { formatDate } from '../../utils/dateUtils';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 40px 20px;
`;

const ContentWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const Hero = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 60px 40px;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 40px 20px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 48px;
  font-weight: 700;
  margin: 0 0 20px 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 18px;
  margin: 0;
  opacity: 0.9;
  font-weight: 300;
`;

const Content = styled.div`
  padding: 60px 40px;
  
  @media (max-width: 768px) {
    padding: 40px 20px;
  }
`;

const LastUpdated = styled.div`
  background: #e8f4fd;
  border-left: 4px solid #667eea;
  padding: 20px;
  margin-bottom: 40px;
  border-radius: 0 10px 10px 0;
  
  p {
    margin: 0;
    color: #2c3e50;
    font-weight: 500;
  }
`;

const Section = styled.section`
  margin-bottom: 50px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 28px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 20px 0;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 3px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 2px;
  }
`;

const SubTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #34495e;
  margin: 30px 0 15px 0;
`;

const Paragraph = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: #34495e;
  margin: 0 0 20px 0;
  text-align: justify;
`;

const List = styled.ul`
  margin: 20px 0;
  padding-left: 30px;
  
  li {
    font-size: 16px;
    line-height: 1.8;
    color: #34495e;
    margin-bottom: 10px;
  }
`;

const HighlightBox = styled.div`
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 10px;
  padding: 20px;
  margin: 30px 0;
  
  h4 {
    color: #856404;
    margin: 0 0 10px 0;
    font-size: 18px;
    font-weight: 600;
  }
  
  p {
    color: #856404;
    margin: 0;
    font-size: 14px;
    line-height: 1.6;
  }
`;

const ContactInfo = styled.div`
  background: #f8f9fa;
  border-radius: 15px;
  padding: 30px;
  margin: 40px 0;
  text-align: center;
  
  h3 {
    color: #2c3e50;
    margin: 0 0 20px 0;
  }
  
  p {
    margin: 10px 0;
    color: #34495e;
  }
  
  a {
    color: #667eea;
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const PrivacyPage: React.FC = () => {
  const currentDate = formatDate(new Date().toISOString());

  return (
    <PageContainer>
      <Helmet>
        <title>Privacy Policy - Infonium | Protecting Your Privacy</title>
        <meta name="description" content="Infonium's Privacy Policy - Learn how we collect, use, and protect your personal information safely and transparently." />
        <meta name="keywords" content="privacy policy, data protection, privacy rights, GDPR, data security, personal information" />
        <meta name="author" content="Infonium" />
        <meta property="og:title" content="Privacy Policy - Infonium" />
        <meta property="og:description" content="Learn how Infonium protects your personal information and privacy with the highest security standards." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : 'https://www.infonium.org/privacy'} />
        <meta property="og:image" content="https://cdn.infonium.org/img/logo_infonium.png" />
        <meta property="og:site_name" content="Infonium" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Privacy Policy - Infonium" />
        <meta name="twitter:description" content="Learn how Infonium protects your personal information and privacy with the highest security standards." />
        <meta name="twitter:image" content="https://cdn.infonium.org/img/logo_infonium.png" />
        <meta name="twitter:site" content="@infonium" />
        <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : 'https://www.infonium.org/privacy'} />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="en" />
      </Helmet>
      <ContentWrapper>
        <Hero>
          <HeroTitle>Privacy Policy</HeroTitle>
          <HeroSubtitle>Protecting Your Privacy and Data</HeroSubtitle>
        </Hero>

        <Content>
          <LastUpdated>
            <p><strong>Last Updated:</strong> {currentDate}</p>
          </LastUpdated>

          <Section>
            <SectionTitle>1. Introduction</SectionTitle>
            <Paragraph>
              Welcome to Infonium. We respect your privacy and are committed to protecting 
              the personal information you share with us. This Privacy Policy explains how 
              we collect, use, store, and protect your information when you use the Infonium website.
            </Paragraph>
            <Paragraph>
              By using our website, you agree to the terms outlined in this Privacy Policy.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>2. Information We Collect</SectionTitle>
            
            <SubTitle>2.1 Information You Provide Directly</SubTitle>
            <List>
              <li>Contact information (name, email) when you subscribe to our newsletter</li>
              <li>Feedback content when you send suggestions or contact us</li>
              <li>Survey information when you participate in our surveys</li>
            </List>

            <SubTitle>2.2 Automatically Collected Information</SubTitle>
            <List>
              <li>IP address and device information</li>
              <li>Browser and operating system information</li>
              <li>Access time and pages viewed</li>
              <li>Cookie data and similar tracking technologies</li>
              <li>Information about how you interact with our content</li>
            </List>

            <SubTitle>2.3 Third-Party Information</SubTitle>
            <Paragraph>
              We may receive information about you from third-party sources such as social networks, 
              advertising partners, or web analytics services.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>3. How We Use Information</SectionTitle>
            <List>
              <li>Provide and improve our services</li>
              <li>Personalize user experience</li>
              <li>Send newsletters and important notifications</li>
              <li>Analyze usage trends and optimize the website</li>
              <li>Protect security and prevent fraud</li>
              <li>Comply with legal requirements</li>
              <li>Display advertisements relevant to your interests</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>4. Google AdSense and Advertising</SectionTitle>
            <Paragraph>
              Our website uses Google AdSense to display advertisements. Google AdSense uses 
              cookies to display ads based on your previous visits to this website or other websites.
            </Paragraph>
            
            <HighlightBox>
              <h4>Important information about Google AdSense:</h4>
              <p>
                Google uses advertising cookies to display interest-based ads. You can opt out 
                of personalized advertising by visiting Google's Ad Settings or at aboutads.info.
              </p>
            </HighlightBox>

            <SubTitle>4.1 Third-Party Advertising Partners</SubTitle>
            <List>
              <li>Google AdSense and other advertising networks</li>
              <li>Analytics service providers like Google Analytics</li>
              <li>Social media platforms (Facebook, Twitter, LinkedIn)</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>5. Cookies and Tracking Technologies</SectionTitle>
            <Paragraph>
              We use cookies and similar technologies to:
            </Paragraph>
            <List>
              <li>Remember your preferences and settings</li>
              <li>Analyze website traffic</li>
              <li>Improve user experience</li>
              <li>Display relevant advertisements</li>
              <li>Protect website security</li>
            </List>
            <Paragraph>
              You can manage cookies through your browser settings. However, disabling cookies 
              may affect the functionality of the website.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>6. Information Sharing</SectionTitle>
            <Paragraph>
              We do not sell, rent, or trade your personal information to third parties for 
              commercial purposes. However, we may share information in the following cases:
            </Paragraph>
            <List>
              <li>With trusted service providers</li>
              <li>When legally required by competent authorities</li>
              <li>To protect our rights and safety and those of our users</li>
              <li>In case of business merger or acquisition</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>7. Information Security</SectionTitle>
            <Paragraph>
              We implement appropriate technical and organizational security measures to protect 
              your personal information from unauthorized access, use, disclosure, alteration, or destruction.
            </Paragraph>
            <List>
              <li>Data encryption during transmission (SSL/TLS)</li>
              <li>Strict access controls</li>
              <li>Regular security monitoring</li>
              <li>Staff training on information security</li>
              <li>Regular data backups</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>8. Your Rights</SectionTitle>
            <Paragraph>
              You have the following rights regarding your personal information:
            </Paragraph>
            <List>
              <li><strong>Right to Access:</strong> Request to view information we have about you</li>
              <li><strong>Right to Rectification:</strong> Request correction of inaccurate information</li>
              <li><strong>Right to Erasure:</strong> Request deletion of your personal information</li>
              <li><strong>Right to Restriction:</strong> Request limitation of information processing</li>
              <li><strong>Right to Object:</strong> Object to information processing for marketing purposes</li>
              <li><strong>Right to Portability:</strong> Request transfer of information to another provider</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>9. Data Retention</SectionTitle>
            <Paragraph>
              We only retain your personal information for as long as necessary to fulfill 
              the purposes outlined in this policy or as required by law.
            </Paragraph>
            <List>
              <li>Account data: Until you request deletion</li>
              <li>Analytics data: Maximum 26 months</li>
              <li>Cookie data: According to each cookie type's settings</li>
              <li>Contact data: 3 years after last contact</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>10. Children Under 13</SectionTitle>
            <Paragraph>
              Our website is not intended for children under 13 years of age. We do not knowingly 
              collect personal information from children under 13. If we discover that we have 
              collected information from a child under 13, we will delete that information immediately.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>11. Policy Updates</SectionTitle>
            <Paragraph>
              We may update this Privacy Policy from time to time. Any significant changes will be 
              notified on the website and via email (if available). We encourage you to review 
              this policy periodically.
            </Paragraph>
          </Section>

          <ContactInfo>
            <h3>Contact Us</h3>
            <p>If you have any questions about this Privacy Policy, please contact us:</p>
            <p><strong>Email:</strong> <a href="mailto:contact@infonium.org">contact@infonium.org</a></p>
            <p><strong>Website:</strong> <a href="https://www.infonium.org">www.infonium.org</a></p>
            <p>We are committed to resolving any privacy concerns promptly and transparently.</p>
          </ContactInfo>
        </Content>
      </ContentWrapper>
    </PageContainer>
  );
};

export default PrivacyPage; 